import React, {useLayoutEffect, useMemo, useRef, useState} from "react";
import Slider from "react-slick";
import classnames from "classnames";
import {Link} from "react-router-dom";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import LazyImage from "../../../../../../../../components/app-specific/lazy-image";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../assets/images/remove.svg";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";

const CompareViewProductGallery = ({
                                       product,
                                       removeProduct,
                                   }) => {
    const [activeImageIndex, setActiveImageIndex] = useState(-1);
    /**@type {React.MutableRefObject<import('react-slick').default>}*/
    const sliderRef = useRef();
    const productLink = useMemo(() => routeFunctions.lookup.single(product?.partNo), [product?.partNo]);

    /**
     * With each change in the images property of the product:
     * - Resets the active image index's state value.
     */
    useLayoutEffect(() => {
        setActiveImageIndex(product?.images?.length ? 0 : -1);
    }, [product?.images])

    /**
     * Changes the active index of the image based on the given offset.
     * @param {number} offset
     */
    const changeImage = (offset) => {
        const length = product?.images?.length ?? 0;
        let newActiveIndex = activeImageIndex + offset;
        if (newActiveIndex >= length) {
            newActiveIndex %= length;
        }
        if (newActiveIndex < 0) {
            newActiveIndex = length + newActiveIndex;
        }
        sliderRef.current.slickGoTo(newActiveIndex);
        setActiveImageIndex(newActiveIndex)
    }

    /**
     * Handles removing a product from the list of items in compare screen:
     * - Shows a confirmation dialog from for user
     * - after confirmation, calls the function regarding this action
     *
     * @param {MouseEvent<HTMLButtonElement>} e
     */
    const getRemoveProductConfirmation = (e) => {
        e.stopPropagation();
        e.preventDefault();
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove the selected product from compare list?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && _remove(),
        );
    }

    const _remove = () => {
        removeProduct(product).then();
    }

    return (
        <>
            <div className={'compare-product-gallery'}>
                <div className={'compare-product-gallery-header'}>
                    <Link to={productLink} className={'part-no mr-2'}>
                        {product?.partNo ?? ''}
                    </Link>
                    {
                        (removeProduct && !!product) &&
                        <IconButton
                            onPressed={getRemoveProductConfirmation}
                            color={'paper.error'}
                        >
                            <RemoveIcon/>
                        </IconButton>
                    }
                </div>
                <div className={'image-container'}>
                    <LazyImage
                        src={product?.images?.[activeImageIndex]?.fileName}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        className={'disable-downloading-image'}
                    />
                </div>
                <div className={'slider-container-wrapper part-image-slider mt-3'}>
                    {
                        ((product?.images?.length ?? 0) > 4) &&
                        <IconButton
                            color={"paper.primary"}
                            className={'part-images-icon prev'}
                            onPressed={() => changeImage(-1)}
                        >
                            <ChevronLeft/>
                        </IconButton>
                    }
                    <div className={'slider-container'}>
                        <Slider
                            swipeToSlide={false}
                            slidesToScroll={1}
                            focusOnSelect={true}
                            infinite={true}
                            arrows={false}
                            draggable={false}
                            slidesToShow={product?.images?.length > 4 ? 4 : product?.images?.length ?? 0}
                            ref={sliderRef}
                            className={'part-image-slider-item'}
                        >
                            {
                                product?.images?.map((item, index) => (
                                    <div
                                        key={item}
                                        className={'image-wrapper'}
                                        onClick={() => changeImage(index - activeImageIndex)}
                                    >
                                        <div className={classnames('img', {'active': index === activeImageIndex})}>
                                            <LazyImage
                                                src={item.fileName}
                                                alt=""
                                                onContextMenu={(e) => e.preventDefault()}
                                                onDragStart={(e) => e.preventDefault()}
                                                className={'disable-downloading-image'}
                                            />
                                        </div>
                                    </div>
                                ))
                            }
                        </Slider>
                    </div>
                    {
                        product?.images?.length > 4 &&
                        <IconButton
                            color={"paper.primary"}
                            className={'part-images-icon next'}
                            onPressed={() => changeImage(1)}
                        >
                            <ChevronRight/>
                        </IconButton>
                    }
                </div>
            </div>
        </>
    )
}

export default CompareViewProductGallery;
